import React, { createContext, useState, useContext, useEffect } from 'react';
import telegramService from '../services/telegram';
import { tgChrome } from '../constants/colors';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

const STORAGE_KEY = 'spbnef_theme';

// Тема телефона: Telegram отдаёт colorScheme, вне Telegram — системная
// настройка браузера. Читается только при запуске.
const getSystemTheme = () => {
  try {
    const tg = telegramService.getTelegramWebApp();
    if (tg?.colorScheme === 'light' || tg?.colorScheme === 'dark') {
      return tg.colorScheme;
    }
  } catch (e) {
    // Telegram недоступен — падаем на системную настройку браузера
  }

  if (window.matchMedia?.('(prefers-color-scheme: light)').matches) {
    return 'light';
  }
  return 'dark';
};

// Выбор пользователя важнее темы телефона. В localStorage тема попадает
// только при явном переключении в профиле, поэтому её наличие и означает,
// что пользователь сделал выбор.
const getInitialTheme = () => {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved === 'light' || saved === 'dark') return saved;
  return getSystemTheme();
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    const isDark = theme === 'dark';
    document.documentElement.classList.toggle('dark', isDark);
    // Обновляем theme-color мета-тег и фон html для корректного оверскролла
    const meta = document.getElementById('theme-color-meta');
    const bgColor = tgChrome(isDark);
    if (meta) meta.setAttribute('content', bgColor);
    document.documentElement.style.backgroundColor = bgColor;
    // Обновляем цвет хедера и фона Telegram WebApp
    telegramService.setThemeColors(isDark);
  }, [theme]);

  // Сохраняем только явный выбор пользователя: пока в localStorage пусто,
  // при следующем запуске снова подхватится тема телефона
  const toggleTheme = () => {
    setTheme(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  };

  const setThemeExplicit = (value) => {
    if (value === 'light' || value === 'dark') {
      localStorage.setItem(STORAGE_KEY, value);
      setTheme(value);
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme: setThemeExplicit, isDark: theme === 'dark' }}>
      {children}
    </ThemeContext.Provider>
  );
};
